import { makePath } from "../../../url";
import * as APITypes from "../../generated-types/v1/Api";
import { applyPagination } from "../../pagination";
import { getJSON, getRaw, postJSON } from "../../request";
import { APIContext } from "../../types";

export type VisitAPI = ReturnType<typeof createVisitAPI>;

export type Flatten<T> = { [P in keyof T]: T[P] } & {};

type GetPatientVisitsQueryParams = Flatten<
  Omit<
    APITypes.VisitGetsByPatientIdParams,
    | "patientId"
    | "startDateTimeOffsetRange.start"
    | "startDateTimeOffsetRange.end"
    | "endDateTimeOffsetRange.start"
    | "endDateTimeOffsetRange.end"
  > & {
    startDateTimeOffsetRange?: DateRange;
    endDateTimeOffsetRange?: DateRange;
  }
>;

type DateRange = {
  start?: string;
  end?: string;
};

export function createVisitAPI({
  buildURL,
  fetchRaw,
  fetchJSON,
  fetchPagedJSON,
}: APIContext) {
  function checkInVisit(visit: {
    visitId: string;
    eTag: string;
    patientId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      visit.patientId,
      "visit",
      visit.visitId,
      "check-in",
    ]);

    const url = buildURL(path);
    const request = postJSON(url, undefined);

    request.headers.set("If-Match", visit.eTag);

    return fetchJSON<APITypes.Visit>(request);
  }

  function checkIn({
    bookingResourceUri,
    slotId,
  }: {
    bookingResourceUri: string;
    slotId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "booking",
      bookingResourceUri,
      "slot",
      slotId,
      "check-in",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, undefined);

    return fetchJSON<APITypes.Visit>(request);
  }

  function fetchVisit({
    patientId,
    visitId,
  }: {
    patientId: string;
    visitId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypes.Visit>(request);
  }

  function getPatientVisits(
    { patientId }: { patientId: string },
    params: GetPatientVisitsQueryParams
  ) {
    const path = makePath(["api", "v1", "patient", patientId, "visit"]);
    const url = buildURL(path);
    const { searchParams } = url;
    const {
      startDateTimeOffsetRange,
      endDateTimeOffsetRange,
      status,
      ...rest
    } = params;

    if (startDateTimeOffsetRange?.start != null) {
      searchParams.append(
        "startDateTimeOffsetRange.start",
        startDateTimeOffsetRange.start
      );
    }

    if (startDateTimeOffsetRange?.end != null) {
      searchParams.append(
        "startDateTimeOffsetRange.end",
        startDateTimeOffsetRange.end
      );
    }

    if (endDateTimeOffsetRange?.start != null) {
      searchParams.append(
        "endDateTimeOffsetRange.start",
        endDateTimeOffsetRange.start
      );
    }

    if (endDateTimeOffsetRange?.end != null) {
      searchParams.append(
        "endDateTimeOffsetRange.end",
        endDateTimeOffsetRange.end
      );
    }

    if (status != null) {
      searchParams.append("status", status);
    }

    applyPagination(searchParams, rest);
    const request = getJSON(url);

    return fetchPagedJSON<APITypes.Visit>(request);
  }

  function updateVisit(
    { patientId, visitId }: { visitId: string; patientId: string },
    payload: APITypes.UpdateVisitRequest
  ) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
    ]);
    const url = buildURL(path);
    const request = postJSON(url, payload);

    return fetchJSON<APITypes.Visit>(request);
  }

  function fetchScanBookedAssetDetails({
    patientId,
    visitId,
  }: {
    patientId: string;
    visitId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
      "assets",
      "scan-booked",
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypes.ScanBookedAsset>(request);
  }

  function fetchScanBookedAsset({
    patientId,
    visitId,
  }: {
    patientId: string;
    visitId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
      "assets",
      "scan-booked.png",
    ]);
    const url = buildURL(path);
    const request = getRaw(url);
    return fetchRaw(request);
  }

  function startCheckInProcess({
    patientId,
    visitId,
  }: {
    patientId: string;
    visitId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
      "start-checkin",
    ]);

    const url = buildURL(path);
    const body: APITypes.StartCheckinProcessRequest = {};
    const request = postJSON(url, body);

    return fetchJSON<APITypes.Visit>(request);
  }

  function endCheckInProcess({
    patientId,
    visitId,
  }: {
    patientId: string;
    visitId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
      "end-checkin",
    ]);

    const url = buildURL(path);
    const body: APITypes.EndCheckinProcessRequest = {};
    const request = postJSON(url, body);

    return fetchJSON<APITypes.Visit>(request);
  }

  function fetchScanCompleteAssetDetails({
    patientId,
    visitId,
  }: {
    patientId: string;
    visitId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
      "assets",
      "scan-complete",
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypes.ScanCompleteAsset>(request);
  }

  function fetchScanCompleteAsset({
    patientId,
    visitId,
  }: {
    patientId: string;
    visitId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "visit",
      visitId,
      "assets",
      "scan-complete.png",
    ]);
    const url = buildURL(path);
    const request = getRaw(url);
    return fetchRaw(request);
  }

  return {
    checkInVisit,
    getPatientVisits,
    updateVisit,
    checkIn,
    fetchVisit,
    fetchScanBookedAssetDetails,
    fetchScanBookedAsset,
    startCheckInProcess,
    endCheckInProcess,
    fetchScanCompleteAssetDetails,
    fetchScanCompleteAsset,
  };
}
